// src/utils/bookingNotifications.js
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import whatsappService from '../api/whatsappService';
import {
  loadSalonNotificationConfig, resolveEmailTemplate, resolvePushTemplate, renderTemplate,
  toJsDate, formatDateInTz, formatTimeInTz, formatMoney
} from './transactionalTemplates';

// Initialize Firebase Functions
const functions = getFunctions();

// Links inside these emails must point at the site the customer actually used (development, staging or
// production), so build them from the current origin instead of a hard-coded production address.
const appBaseUrl = () => ((typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : 'https://bespokebeauty.cloud');

// Function to send booking notification email via Firebase Functions
export const sendBookingNotificationEmail = async ({
  customerEmail,
  customerName,
  stylistName,
  serviceName,
  appointmentDateTime,
  status,
  salonId
}) => {
  try {
    const emailData = {
      customerEmail,
      customerName,
      stylistName,
      serviceName,
      appointmentDateTime: appointmentDateTime.toISOString(),
      status,
      salonId
    };

    console.log('Sending email notification:', emailData);

    // Call the Firebase Function to send email
    const sendEmailFunction = httpsCallable(functions, 'sendBookingNotificationEmail');
    const result = await sendEmailFunction(emailData);
    
    console.log('Email sent successfully:', result.data);
    return result.data;

  } catch (error) {
    console.error('Error sending booking notification email:', error);
    
    // Fallback: Create a local email log for debugging
    try {
      const emailLogRef = collection(db, 'emailLogs');
      await addDoc(emailLogRef, {
        customerEmail,
        customerName,
        stylistName,
        serviceName,
        appointmentDateTime: appointmentDateTime.toLocaleString(),
        status,
        salonId,
        sentAt: serverTimestamp(),
        type: 'booking_notification',
        subject: status === 'approved' 
          ? `Appointment Confirmed - ${serviceName} with ${stylistName}`
          : `Appointment Update - ${serviceName}`,
        template: status === 'approved' ? 'booking_approved' : 'booking_rejected',
        error: error.message,
        fallback: true
      });
      
      console.log('Email log created as fallback');
    } catch (logError) {
      console.error('Error creating email log:', logError);
    }
    
    // Don't throw the error to prevent blocking the booking approval
    return { success: false, error: error.message };
  }
};

// Backend API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Function to send push notification when appointment status changes
export const sendBookingPushNotification = async ({
  customerEmail,
  salonId,
  status,
  serviceName,
  staffName,
  appointmentDate
}) => {
  try {
    let title, body;

    if (status === 'approved') {
      title = 'Appointment Confirmed!';
      body = `Your ${serviceName} appointment on ${appointmentDate} with ${staffName} has been approved. See you soon!`;
    } else if (status === 'rejected') {
      title = 'Appointment Update';
      body = `Your ${serviceName} appointment request on ${appointmentDate} with ${staffName} could not be accommodated. Please rebook at another time.`;
    } else {
      return { success: false, message: 'Unsupported status for push notification' };
    }

    console.log('Sending booking push notification:', { customerEmail, salonId, title, body });

    const response = await fetch(`${API_BASE_URL}/api/notifications/send-push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerEmail,
        salonId,
        title,
        body,
        data: {
          type: status === 'approved' ? 'booking_approval' : 'booking_rejection',
          salonId
        }
      })
    });

    const result = await response.json();
    console.log('Push notification result:', result);
    return result;

  } catch (error) {
    console.error('Error sending booking push notification:', error);
    return { success: false, error: error.message };
  }
};

// Generic function to send a transactional push notification to a specific customer
export const sendTransactionalPushNotification = async ({
  customerEmail,
  salonId,
  title,
  body,
  data = {}
}) => {
  try {
    if (!customerEmail || !salonId || !title || !body) {
      return { success: false, message: 'Missing required fields' };
    }

    console.log('Sending transactional push notification:', { customerEmail, salonId, title });

    const response = await fetch(`${API_BASE_URL}/api/notifications/send-push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerEmail,
        salonId,
        title,
        body,
        data: { ...data, salonId }
      })
    });

    const result = await response.json();
    console.log('Transactional push notification result:', result);
    return result;

  } catch (error) {
    console.error('Error sending transactional push notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Tell a customer their appointment was approved or rejected: push notification + e-mail, each only if enabled in
 * the salon's transactionalCampaignSettings (plus WhatsApp when appointmentId is given). Used by Calendar Management, Booking Requests and the Bookings AI agent.
 * Templates come from the salon document (emailTemplates / pushTemplates), dates/times are rendered in the salon's
 * timezone. Never throws - notification failures must not block the booking change itself.
 */
export const notifyCustomerOfAppointmentStatus = async ({
  salonId,
  status,
  customerEmail,
  customerName,
  serviceName,
  staffName,
  appointmentDateTime,
  appointmentId
}) => {
  if (status !== 'approved' && status !== 'rejected' && status !== 'cancelled') return;

  // WhatsApp (approved -> booking_confirmed; rejected -> booking_rejected; cancelled -> booking_cancelled). The backend resolves the
  // values from the appointment and applies the salon toggle + the customer's WhatsApp opt-in. Non-blocking.
  if (appointmentId) {
    whatsappService.sendTransactional(
      { templateId: { approved: 'booking_confirmed', rejected: 'booking_rejected', cancelled: 'booking_cancelled' }[status], appointmentId },
      salonId
    );
  }

  // Cancellations have no push/e-mail template - WhatsApp only
  if (status === 'cancelled') return;

  try {
    const cfg = await loadSalonNotificationConfig(salonId);
    const when = toJsDate(appointmentDateTime);
    const validWhen = when && !Number.isNaN(when.getTime());
    const vars = {
      name: customerName || customerEmail?.split('@')[0] || 'Valued Customer',
      date: validWhen ? formatDateInTz(when, cfg.timeZone) : '',
      time: validWhen ? formatTimeInTz(when, cfg.timeZone) : '',
      service: serviceName || 'your service',
      staff: staffName || 'our stylist'
    };

    const pushTemplateId = status === 'approved' ? 'appointment_approved' : 'appointment_rejected';
    if (cfg.settings[`push_${pushTemplateId}`] !== false && customerEmail) {
      const pushTemplate = resolvePushTemplate(cfg, pushTemplateId);
      sendTransactionalPushNotification({
        customerEmail,
        salonId,
        title: renderTemplate(pushTemplate.title, vars),
        body: renderTemplate(pushTemplate.body, vars),
        data: { type: status === 'approved' ? 'booking_approval' : 'booking_rejection' }
      }).catch(err => console.error('Push notification failed (non-blocking):', err));
    }

    const templateId = status === 'approved' ? 'appointment_confirmation' : 'appointment_rejected';
    if (cfg.settings[`email_${templateId}`] === false) {
      console.log(`Email campaign ${templateId} is disabled, skipping`);
      return;
    }
    if (!customerEmail) return;

    const template = resolveEmailTemplate(cfg, templateId);

    // Write to mail collection — sendEmail Cloud Function auto-triggers
    await addDoc(collection(db, 'mail'), {
      to: customerEmail,
      salonId,
      message: {
        subject: renderTemplate(template.subject, vars),
        html: renderTemplate(template.html, vars),
        text: renderTemplate(template.body, vars)
      },
      metadata: {
        campaignType: 'booking_notification',
        salonId,
        customerId: `${customerEmail.toLowerCase()}_${salonId}`
      }
    });
    console.log(`Booking ${status} email queued for ${customerEmail}`);
  } catch (err) {
    console.error('Booking notification failed (non-blocking):', err);
  }
};

/**
 * "Wallet Debited" push. Honours the salon's push_wallet_debit toggle and its (editable) wallet_debit template.
 * Used by both the admin transaction screens and the customer's own wallet purchase. Never throws.
 */
export const notifyWalletDebit = async ({ salonId, customerEmail, customerName, amount, newBalance, item }) => {
  try {
    if (!customerEmail || !salonId) return;
    const cfg = await loadSalonNotificationConfig(salonId);
    if (cfg.settings.push_wallet_debit === false) return;
    const template = resolvePushTemplate(cfg, 'wallet_debit');
    const vars = {
      name: customerName || customerEmail.split('@')[0],
      amount: formatMoney(amount),
      balance: formatMoney(newBalance),
      item: item || 'your purchase'
    };
    await sendTransactionalPushNotification({
      customerEmail,
      salonId,
      title: renderTemplate(template.title, vars),
      body: renderTemplate(template.body, vars),
      data: { type: 'wallet_debit' }
    });
  } catch (err) {
    console.error('Wallet debit push failed (non-blocking):', err);
  }
};

/**
 * Wallet top-up verified / rejected push. Verified uses push_wallet_topup + wallet_topup template;
 * rejected uses push_wallet_topup_rejected + wallet_topup_rejected template. Never throws.
 */
export const notifyWalletTopup = async ({ salonId, customerEmail, customerName, amount, verified, newBalance }) => {
  try {
    if (!customerEmail || !salonId) return;
    const cfg = await loadSalonNotificationConfig(salonId);
    const templateId = verified ? 'wallet_topup' : 'wallet_topup_rejected';
    if (cfg.settings[`push_${templateId}`] === false) return;
    const template = resolvePushTemplate(cfg, templateId);
    const vars = {
      name: customerName || customerEmail.split('@')[0],
      amount: formatMoney(amount),
      balance: formatMoney(newBalance)
    };
    await sendTransactionalPushNotification({
      customerEmail,
      salonId,
      title: renderTemplate(template.title, vars),
      body: renderTemplate(template.body, vars),
      data: { type: verified ? 'wallet_topup_verified' : 'wallet_topup_rejected' }
    });
  } catch (err) {
    console.error('Wallet top-up push failed (non-blocking):', err);
  }
};

// Firebase Function for sending emails (this would be deployed to Firebase Functions)
// Place this in functions/src/index.js
export const firebaseFunctionCode = `
const functions = require('firebase-functions');
const admin = require('firebase-admin');
const sgMail = require('@sendgrid/mail');

// Initialize Firebase Admin
if (!admin.apps.length) {
  admin.initializeApp();
}

// Set SendGrid API key
sgMail.setApiKey(functions.config().sendgrid.key);

exports.sendBookingNotificationEmail = functions.https.onCall(async (data, context) => {
  try {
    const { 
      to, 
      customerName, 
      stylistName, 
      serviceName, 
      appointmentDateTime, 
      status,
      salonId 
    } = data;

    // Get salon configuration for email branding
    const salonDoc = await admin.firestore()
      .collection('salons')
      .doc(salonId)
      .get();
    
    const salonData = salonDoc.exists ? salonDoc.data() : {};
    const salonName = salonData.branding?.name || 'Beauty Salon';
    const salonEmail = salonData.contact?.email || 'noreply@salon.com';

    let subject, html;

    if (status === 'approved') {
      subject = \`Appointment Confirmed - \${serviceName} with \${stylistName}\`;
      html = \`
        <h2>Appointment Confirmed!</h2>
        <p>Dear \${customerName},</p>
        <p>Great news! Your appointment has been confirmed.</p>
        
        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3>Appointment Details:</h3>
          <ul style="list-style: none; padding: 0;">
            <li><strong>Service:</strong> \${serviceName}</li>
            <li><strong>Stylist:</strong> \${stylistName}</li>
            <li><strong>Date & Time:</strong> \${appointmentDateTime}</li>
            <li><strong>Salon:</strong> \${salonName}</li>
          </ul>
        </div>
        
        <p>We look forward to seeing you soon!</p>
        <p>If you need to make any changes, please contact us as soon as possible.</p>

        <p>Best regards,<br>
        The \${salonName} Team</p>

      \`;
    } else if (status === 'rejected') {
      subject = \`Appointment Request Update - \${serviceName}\`;
      html = \`
        <h2>Appointment Request Update</h2>
        <p>Dear \${customerName},</p>
        <p>We regret to inform you that your appointment request could not be accommodated at the requested time.</p>
        
        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3>Original Request:</h3>
          <ul style="list-style: none; padding: 0;">
            <li><strong>Service:</strong> \${serviceName}</li>
            <li><strong>Stylist:</strong> \${stylistName}</li>
            <li><strong>Requested Time:</strong> \${appointmentDateTime}</li>
          </ul>
        </div>
        
        <p>Please feel free to book another appointment at a different time that works for you.</p>
        <p>We apologize for any inconvenience and look forward to serving you soon.</p>

        <p>Best regards,<br>
        The \${salonName} Team</p>

      \`;
    }

    const msg = {
      to,
      from: {
        email: salonEmail,
        name: salonName
      },
      subject,
      html
    };

    await sgMail.send(msg);
    
    console.log('Email sent successfully to:', to);
    return { success: true, message: 'Email sent successfully' };

  } catch (error) {
    console.error('Error sending email:', error);
    throw new functions.https.HttpsError('internal', 'Failed to send email');
  }
});
`;